import { useState, useEffect } from 'react';
import api from '../../services/api';
import Modal from '../UI/Modal';

export default function PatientManager({ onSelectPatient }) {
    const [patients, setPatients] = useState([]);
    const [secteurs, setSecteurs] = useState([]);
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [filtreSecteur, setFiltreSecteur] = useState('');
    const [filtrePathologie, setFiltrePathologie] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [geocoding, setGeocoding] = useState(false);
    const [geocodeResult, setGeocodeResult] = useState(null);

    // ── Création d'un nouveau patient ──────────────────────────────────────
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState(emptyForm());
    const [filtreLivreur, setFiltreLivreur] = useState('');
    const [vueArchives, setVueArchives] = useState(false);

    function emptyForm() {
        return {
            nom: '', ville: '', adresse: '',
            adresseSecondaire: '', villeSecondaire: '',
            telephone: '',
            actif: true, pathologie: '', statutPatient: 'actif', secteur: '', chu: '', livreur: '', molecule: '',
            isRetro: false,
        };
    }

    useEffect(() => {
        loadPatients();
        loadSecteurs();
        loadUsers();
    }, []);

    const loadPatients = async () => {
        try {
            const res = await api.get('/patients');
            setPatients(res.data.member || []);
        } catch (err) { console.error(err); }
    };

    const loadSecteurs = async () => {
        try {
            const res = await api.get('/secteurs');
            setSecteurs(res.data.member || []);
        } catch (err) { console.error(err); }
    };

    const loadUsers = async () => {
        try {
            const res = await api.get('/users');
            setUsers(res.data.member || []);
        } catch (err) { console.error(err); }
    };

    const filteredPatients = patients.filter(p => {
        const isArchiveOuFtt = !p.actif || p.statutPatient === 'ftt';
        if (vueArchives) return isArchiveOuFtt;
        if (isArchiveOuFtt) return false;
        const searchMatch = p.nom?.toLowerCase().includes(search.toLowerCase()) ||
            p.ville?.toLowerCase().includes(search.toLowerCase());
        const secteurMatch = !filtreSecteur || p.secteur?.['@id'] === filtreSecteur;
        const pathologieMatch = !filtrePathologie || p.pathologie === filtrePathologie;
        const livreurMatch = !filtreLivreur ||
            (filtreLivreur === 'aucun' ? !p.livreur : p.livreur?.['@id'] === filtreLivreur);
        return searchMatch && secteurMatch && pathologieMatch && livreurMatch;
    }).sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));

    // ── Géolocalisation ──────────────────────────────────────────────────
    const handleGeocode = async (patient) => {
        try {
            const id = patient.id || patient['@id']?.split('/').pop();
            await api.post(`/patients/${id}/geocode`);
            loadPatients();
        } catch (err) {
            console.error(err);
            alert('Géolocalisation impossible pour ce patient — vérifiez l\'adresse.');
        }
    };

    const handleGeocodeBulk = async () => {
        if (!window.confirm('Géolocaliser tous les patients actifs sans coordonnées ? Cela peut prendre quelques minutes.')) return;
        setGeocoding(true);
        try {
            const res = await api.post('/patients/geocode-bulk');
            setGeocodeResult(res.data);
            loadPatients();
        } catch (err) {
            console.error(err);
        } finally {
            setGeocoding(false);
        }
    };

    // ── Création ─────────────────────────────────────────────────────────
    const openCreate = () => {
        setCreateForm(emptyForm());
        setShowCreateModal(true);
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const payload = {
                ...createForm,
                livreur: createForm.livreur || null,
                secteur: createForm.secteur || null,
                chu: createForm.chu || null,
            };
            await api.post('/patients', payload, { headers: { 'Content-Type': 'application/ld+json' } });
            setShowCreateModal(false);
            setCreateForm(emptyForm());
            loadPatients();
        } catch (err) {
            console.error(err);
        } finally {
            setCreating(false);
        }
    };

    // ── Archivage ────────────────────────────────────────────────────────
    const handleDelete = async (patient) => {
        try {
            const id = patient.id || patient['@id']?.split('/').pop();
            await api.patch(`/patients/${id}`, { actif: false }, { headers: { 'Content-Type': 'application/merge-patch+json' } });
            setConfirmDelete(null);
            loadPatients();
        } catch (err) { console.error(err); }
    };

    const statutConfig = {
        actif:       { label: '✅ Actif',        bg: '#dcfce7', color: '#16a34a' },
        en_pause:    { label: '⏸️ En pause',     bg: '#fef9c3', color: '#854d0e' },
        hospitalise: { label: '🏥 Hospitalisé',  bg: '#fee2e2', color: '#dc2626' },
        ftt:         { label: '🔴 FTT',           bg: '#fee2e2', color: '#dc2626' },
    };

    const getStatutBadge = (p) => {
        if (!p.actif) return { label: '📦 Archivé', bg: '#f0f0f0', color: '#888' };
        return statutConfig[p.statutPatient] || { label: p.statutPatient, bg: '#f0f0f0', color: '#888' };
    };

    const masquerAdresse = (adresse) => {
        if (!adresse) return '—';
        const match = adresse.match(/^(\d+\s+\w+\s+)/);
        if (!match) return '***';
        return match[1] + '***';
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>
                    Patients ({filteredPatients.length}{filteredPatients.length !== patients.length ? ` / ${patients.length}` : ''})
                </h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={handleGeocodeBulk} disabled={geocoding} style={{ ...addButtonStyle, backgroundColor: geocoding ? '#a5b4fc' : '#16a34a' }}>
                        <><i className="ti ti-map-pin" style={{ marginRight: '6px' }} />{geocoding ? 'Géolocalisation en cours...' : 'Géolocaliser tous les patients'}</>
                    </button>
                    <button onClick={openCreate} style={addButtonStyle}>
                        <i className="ti ti-plus" style={{marginRight: '6px'}}/>Ajouter un patient
                    </button>
                </div>
            </div>

            <div style={{display: 'flex', gap: '12px', marginBottom: '16px'}}>
                <input
                    type="text"
                    placeholder="🔍 Rechercher par nom ou ville..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                        flex: 2,
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                        backgroundColor: 'white'
                    }}
                />
                <select
                    value={filtreSecteur}
                    onChange={e => setFiltreSecteur(e.target.value)}
                    style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        fontSize: '14px',
                        backgroundColor: filtreSecteur ? '#ede9fe' : 'white',
                        color: filtreSecteur ? '#4f46e5' : '#666',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">🗺️ Tous les secteurs</option>
                    {secteurs.map(s => <option key={s['@id']} value={s['@id']}>{s.nom}</option>)}
                </select>
                <select
                    value={filtreLivreur}
                    onChange={e => setFiltreLivreur(e.target.value)}
                    style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        fontSize: '14px',
                        backgroundColor: filtreLivreur ? '#ede9fe' : 'white',
                        color: filtreLivreur ? '#4f46e5' : '#666',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">👤 Tous les livreurs</option>
                    {users.map(u => <option key={u['@id']}
                                            value={u['@id']}>{u.nom || u.email}</option>)}
                    <option value="aucun">— Sans livreur attitré —</option>
                </select>
                <select
                    value={filtrePathologie}
                    onChange={e => setFiltrePathologie(e.target.value)}
                    style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        fontSize: '14px',
                        backgroundColor: filtrePathologie ? '#ede9fe' : 'white',
                        color: filtrePathologie ? '#4f46e5' : '#666',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">🏥 Toutes pathologies</option>
                    <option value="nutrition">🟢 Nutrition</option>
                    <option value="perfusion">🔵 Perfusion</option>
                    <option value="parkinson">⚫ Parkinson</option>
                </select>
                <button
                    onClick={() => setVueArchives(!vueArchives)}
                    style={{
                        ...addButtonStyle,
                        backgroundColor: vueArchives ? '#f59e0b' : '#f0f2f5',
                        color: vueArchives ? 'white' : '#666',
                    }}
                >
                    <i className="ti ti-archive" style={{marginRight: '6px'}}/>
                    {vueArchives ? 'Vue archives/FTT' : 'Voir archives/FTT'}
                </button>
            </div>

            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)'
            }}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                    <thead>
                    <tr style={{backgroundColor: '#f8fafc'}}>
                        <th style={thStyle}>Nom</th>
                        <th style={thStyle}>Ville</th>
                        <th style={thStyle}>Adresse</th>
                        <th style={thStyle}>Téléphone</th>
                        <th style={thStyle}>Pathologie</th>
                        <th style={thStyle}>Statut</th>
                        <th style={thStyle}>Secteur</th>
                        <th style={thStyle}>Attitré</th>
                        <th style={thStyle}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredPatients.map(p => {
                        const badge = getStatutBadge(p);
                        return (
                            <tr key={p.id} style={{borderTop: '1px solid #f0f0f0' }}>
                                <td style={{ ...tdStyle, cursor: 'pointer', color: 'var(--accent-color)', fontWeight: '600' }} onClick={() => onSelectPatient(p.id)}>
                                    {p.nom}
                                </td>
                                <td style={tdStyle}>{p.ville}</td>
                                <td style={tdStyle}>{masquerAdresse(p.adresse)}</td>
                                <td style={tdStyle}>{p.telephone || '—'}</td>
                                <td style={tdStyle}>
                                    {p.pathologie ? (
                                        <span style={{
                                            padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                                            backgroundColor: p.pathologie === 'nutrition' ? '#dcfce7' : p.pathologie === 'perfusion' ? '#dbeafe' : '#f0f2f5',
                                            color: p.pathologie === 'nutrition' ? '#16a34a' : p.pathologie === 'perfusion' ? '#2563eb' : '#1a1a2e',
                                        }}>{p.pathologie}</span>
                                    ) : '—'}
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', backgroundColor: badge.bg, color: badge.color }}>
                                        {badge.label}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    {p.secteur ? (
                                        <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', backgroundColor: (p.secteur.couleur || '#888') + '22', color: p.secteur.couleur || '#888' }}>
                                            {p.secteur.nom}
                                        </span>
                                    ) : '—'}
                                </td>
                                <td style={tdStyle}>
                                    {p.livreur ? (
                                        <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--accent-color)', backgroundColor: '#ede9fe', padding: '3px 10px', borderRadius: '20px' }}>
                                            {p.livreur.nom || p.livreur.email}
                                        </span>
                                    ) : '—'}
                                </td>
                                <td style={tdStyle}>
                                    <div style={{display: 'flex', gap: '8px'}}>
                                        <button onClick={() => onSelectPatient(p.id)} style={editButtonStyle}
                                                title="Modifier">
                                            <i className="ti ti-pencil"/>
                                        </button>
                                        <button
                                            onClick={() => handleGeocode(p)}
                                            style={{
                                                ...editButtonStyle,
                                                backgroundColor: p.latitude ? '#dcfce7' : '#fef9c3'
                                            }}
                                            title={p.latitude ? 'Re-géolocaliser' : 'Non géolocalisé'}
                                        >
                                            <i className="ti ti-map-pin"/>
                                        </button>
                                        <button onClick={() => setConfirmDelete(p)} style={deleteButtonStyle}
                                                title="Archiver">
                                            <i className="ti ti-archive"/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    {patients.length === 0 && (
                        <tr>
                            <td colSpan={9} style={{...tdStyle, textAlign: 'center', color: '#888', padding: '32px'}}>
                                Aucun patient enregistré
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {geocodeResult && (
                <div style={{
                    marginTop: '16px',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#f0fdf4',
                    fontSize: '13px',
                    color: '#16a34a'
                }}>
                    <i className="ti ti-check" style={{
                        marginRight: '4px',
                        color: '#16a34a'
                    }}/> {geocodeResult.success.length} patient{geocodeResult.success.length > 1 ? 's' : ''} géolocalisé{geocodeResult.success.length > 1 ? 's' : ''}
                    {geocodeResult.failed.length > 0 && (
                        <span style={{color: '#dc2626', marginLeft: '12px'}}>
                            <i className="ti ti-alert-triangle" style={{
                                marginRight: '4px',
                                color: '#d97706'
                            }}/> {geocodeResult.failed.length} échec{geocodeResult.failed.length > 1 ? 's' : ''} : {geocodeResult.failed.map(f => f.nom).join(', ')}
                        </span>
                    )}
                </div>
            )}

            {/* Confirmation archivage */}
            {confirmDelete && (
                <Modal title="Archiver le patient" onClose={() => setConfirmDelete(null)}>
                    <p style={{ marginBottom: '24px', color: '#444' }}>
                        Archiver <strong>{confirmDelete.nom}</strong> ? Il n'apparaîtra plus dans les listes de sélection, mais son historique de livraisons sera conservé.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setConfirmDelete(null)} style={cancelButtonStyle}>Annuler</button>
                        <button onClick={() => handleDelete(confirmDelete)} style={{ ...submitButtonStyle, backgroundColor: '#ef4444' }}>Archiver</button>
                    </div>
                </Modal>
            )}

            {/* Création d'un nouveau patient — compactée en 2 colonnes */}
            {showCreateModal && (
                <Modal title="Nouveau patient" onClose={() => setShowCreateModal(false)} gradient={true}>
                    <form onSubmit={handleCreateSubmit}>
                        {/* Section : Identité */}
                        <div style={sectionStyle}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ ...fieldStyle, flex: 1 }}>
                                    <label style={labelStyle}>Nom *</label>
                                    <input type="text" value={createForm.nom}
                                           onChange={e => setCreateForm({ ...createForm, nom: e.target.value })}
                                           style={inputStyle} placeholder="Dupont" required />
                                </div>
                                <div style={{ ...fieldStyle, flex: 1 }}>
                                    <label style={labelStyle}>Téléphone</label>
                                    <input type="tel" value={createForm.telephone}
                                           onChange={e => setCreateForm({ ...createForm, telephone: e.target.value })}
                                           style={inputStyle} placeholder="06 12 34 56 78" />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ ...fieldStyle, flex: 2 }}>
                                    <label style={labelStyle}>Adresse *</label>
                                    <input type="text" value={createForm.adresse}
                                           onChange={e => setCreateForm({ ...createForm, adresse: e.target.value })}
                                           style={inputStyle} placeholder="12 rue de la Paix" required />
                                </div>
                                <div style={{ ...fieldStyle, flex: 1 }}>
                                    <label style={labelStyle}>Ville *</label>
                                    <input type="text" value={createForm.ville}
                                           onChange={e => setCreateForm({ ...createForm, ville: e.target.value })}
                                           style={inputStyle} placeholder="Lyon" required />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ ...fieldStyle, flex: 2, marginBottom: 0 }}>
                                    <label style={labelStyle}>Adresse secondaire</label>
                                    <input type="text" value={createForm.adresseSecondaire}
                                           onChange={e => setCreateForm({ ...createForm, adresseSecondaire: e.target.value })}
                                           style={inputStyle} placeholder="Résidence secondaire..." />
                                </div>
                                <div style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}>
                                    <label style={labelStyle}>Ville secondaire</label>
                                    <input type="text" value={createForm.villeSecondaire}
                                           onChange={e => setCreateForm({ ...createForm, villeSecondaire: e.target.value })}
                                           style={inputStyle} />
                                </div>
                            </div>
                        </div>

                        {/* Section : Suivi médical */}
                        <div style={sectionStyle}>
                            <div style={{display: 'flex', gap: '12px'}}>
                                <div style={{...fieldStyle, flex: 1}}>
                                    <label style={labelStyle}>Pathologie</label>
                                    <select value={createForm.pathologie}
                                            onChange={e => setCreateForm({...createForm, pathologie: e.target.value})}
                                            style={inputStyle}>
                                        <option value="">— Non définie —</option>
                                        <option value="nutrition">🟢 Nutrition</option>
                                        <option value="perfusion">🔵 Perfusion</option>
                                        <option value="parkinson">⚫ Parkinson</option>
                                    </select>
                                </div>
                                <div style={{...fieldStyle, flex: 1}}>
                                    <label style={labelStyle}>Statut patient</label>
                                    <select value={createForm.statutPatient}
                                            onChange={e => setCreateForm({
                                                ...createForm,
                                                statutPatient: e.target.value
                                            })}
                                            style={inputStyle}>
                                        <option value="actif">✅ Actif</option>
                                        <option value="en_pause">⏸️ En pause</option>
                                        <option value="hospitalise">🏥 Hospitalisé</option>
                                        <option value="ftt">🔴 FTT</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{display: 'flex', gap: '12px'}}>
                                <div style={{...fieldStyle, flex: 1}}>
                                    <label style={labelStyle}>CHU Rétrocession</label>
                                    <select value={createForm.chu}
                                            onChange={e => setCreateForm({...createForm, chu: e.target.value})}
                                            style={inputStyle}>
                                        <option value="">— Aucun —</option>
                                        <option value="nancy">🟡 Nancy</option>
                                        <option value="epinal">🔵 Épinal</option>
                                        <option value="bar_le_duc">🟢 Bar-le-Duc</option>
                                        <option value="neufchateau">🔴 Neufchâteau</option>
                                    </select>
                                </div>
                                <div style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}>
                                    <label style={labelStyle}>Molécule</label>
                                    <select value={createForm.molecule}
                                            onChange={e => setCreateForm({ ...createForm, molecule: e.target.value })}
                                            style={inputStyle}>
                                        <option value="">— Aucune —</option>
                                        <option value="C">C — Cuvitru</option>
                                        <option value="X">X — Xembify</option>
                                        <option value="H">H — Hizentra</option>
                                        <option value="CUT">CUT — Cutaquig</option>
                                        <option value="CLAI">CLAI — Clairyg</option>
                                        <option value="P">P — Prolastin</option>
                                        <option value="A">A — Alfalastin</option>
                                        <option value="R">R — Reespreza</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Section : Organisation */}
                        <div style={sectionStyle}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ ...fieldStyle, flex: 1 }}>
                                    <label style={labelStyle}>Secteur</label>
                                    <select value={createForm.secteur}
                                            onChange={e => setCreateForm({ ...createForm, secteur: e.target.value })}
                                            style={inputStyle}>
                                        <option value="">— Non défini —</option>
                                        {secteurs.map(s => <option key={s['@id']} value={s['@id']}>{s.nom}</option>)}
                                    </select>
                                </div>
                                <div style={{ ...fieldStyle, flex: 1 }}>
                                    <label style={labelStyle}>Livreur attitré</label>
                                    <select value={createForm.livreur}
                                            onChange={e => setCreateForm({ ...createForm, livreur: e.target.value })}
                                            style={inputStyle}>
                                        <option value="">— Aucun —</option>
                                        {users.map(u => <option key={u['@id']} value={u['@id']}>{u.nom || u.email}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ ...fieldStyle, marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    id="isRetro"
                                    checked={createForm.isRetro || false}
                                    onChange={e => setCreateForm({ ...createForm, isRetro: e.target.checked })}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                <label htmlFor="isRetro" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>
                                    🔴 Patient rétrocession (affiché en priorité et en rouge)
                                </label>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => setShowCreateModal(false)} style={cancelButtonStyle}>
                                Annuler
                            </button>
                            <button type="submit" style={submitButtonStyle} disabled={creating}>
                                {creating ? 'Création...' : 'Créer'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}

const sectionStyle = {
    borderLeft: '3px solid #4f46e5',
    paddingLeft: '14px',
    marginBottom: '16px',
};
const addButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
};
const editButtonStyle = {
    padding: '4px 8px',
    backgroundColor: '#f0f2f5',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
};
const deleteButtonStyle = {
    padding: '4px 8px',
    backgroundColor: '#fee2e2',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
};
const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#888' };
const tdStyle = { padding: '12px 16px', fontSize: '14px', color: '#333' };
const fieldStyle = { marginBottom: '12px' };
const labelStyle = { display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#444' };
const inputStyle = { width: '100%', padding: '8px 11px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', boxSizing: 'border-box' };
const cancelButtonStyle = { padding: '9px 18px', backgroundColor: '#f0f2f5', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' };
const submitButtonStyle = { padding: '9px 18px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' };